import EcommField from "./EcommField";
import React, { useEffect, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import UploadFile from "@mui/icons-material/UploadFile";
import axios from "axios";
import { EcommSettings } from "../../models/order-crm.model";
import { orderCrmApi } from "../../services/order-crm.service";

const errorMessage = (error: unknown) => {
  const detail = axios.isAxiosError(error) ? error.response?.data?.detail : undefined;
  return typeof detail === "string" ? detail : "Unable to complete this request. Please try again.";
};
export default function PaymentDeliverySettings({ businessId }: { businessId: string }) {
  const [settings, setSettings] = useState<EcommSettings | null>(null);
  const [settingsError, setSettingsError] = useState("");
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const businessRef = useRef(businessId);
  businessRef.current = businessId;
  useEffect(() => {
    let active = true;
    setSettings(null);
    setSettingsError("");
    setSettingsBusy(false);
    if (businessId) orderCrmApi.settings(businessId).then(value => { if (active) setSettings(value); })
      .catch(e => { if (active) setSettingsError(errorMessage(e)); });
    return () => { active = false; };
  }, [businessId]);

  const saveSettings = async () => {
    if (!settings) return;
    setSettingsBusy(true); setError("");
    try { setSettings(await orderCrmApi.saveSettings(businessId, settings)); setNotice("Payment and delivery settings saved."); }
    catch (e) { setError(errorMessage(e)); }
    finally { setSettingsBusy(false); }
  };
  const uploadQr = async (file?: File) => {
    if (!file || !settings) return;
    if (!["image/png", "image/jpeg"].includes(file.type) || file.size > 5 * 1024 * 1024 || file.size === 0) {
      setError("Choose a PNG or JPEG scanner photo up to 5 MB."); return;
    }
    const target = businessId;
    setSettingsBusy(true); setError("");
    try {
      const { paymentQrImage } = await orderCrmApi.uploadPaymentQr(target, file);
      if (businessRef.current !== target) return;
      setSettings(current => current ? { ...current, paymentQrImage } : current);
      setNotice("Scanner photo saved. It will be sent with payment details in chat.");
    } catch (e) { if (businessRef.current === target) setError(errorMessage(e)); }
    finally { if (businessRef.current === target) setSettingsBusy(false); }
  };
  const removeQr = async () => {
    const target = businessId;
    setSettingsBusy(true); setError("");
    try {
      await orderCrmApi.removePaymentQr(target);
      if (businessRef.current !== target) return;
      setSettings(current => current ? { ...current, paymentQrImage: null } : current);
      setNotice("Scanner photo removed.");
    } catch (e) { if (businessRef.current === target) setError(errorMessage(e)); }
    finally { if (businessRef.current === target) setSettingsBusy(false); }
  };

  return <Stack spacing={2}>
    {error && <Alert severity="error">{error}</Alert>}
    {notice && <Alert severity="success" onClose={() => setNotice("")}>{notice}</Alert>}
      <Accordion><AccordionSummary expandIcon={<ExpandMore />}><Typography fontWeight={600}>Payment & delivery settings</Typography></AccordionSummary>
        <AccordionDetails>{settings ? <Stack spacing={2}>
          <EcommField label="Payment instructions sent in chat" multiline minRows={3} value={settings.paymentInstructions} inputProps={{ maxLength: 2000 }}
            placeholder="UPI ID, bank details or payment link, and recipient name" onChange={e => setSettings({ ...settings, paymentInstructions: e.target.value })}
            helperText="Leave blank for the team to share payment details manually." />
          <Box>
            <Typography fontWeight={600}>Payment scanner photo</Typography>
            <Typography variant="body2" color="text.secondary">Upload your store's payment QR code as a PNG or JPEG, up to 5 MB. It is sent with payment instructions and when customers ask for payment details.</Typography>
            {settings.paymentQrImage && <Box sx={{ mt: 1 }}>
              {settings.paymentQrImage.url ? <Box component="img" src={settings.paymentQrImage.url} alt="Store payment QR code" sx={{ maxWidth: 240, maxHeight: 240, objectFit: "contain", display: "block" }} /> :
                <Alert severity="info">Scanner preview is unavailable. Payment text can still be saved; check image storage or replace the photo.</Alert>}
              <Typography variant="caption">{settings.paymentQrImage.filename}</Typography>
            </Box>}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button component="label" startIcon={<UploadFile />} disabled={settingsBusy}>{settings.paymentQrImage ? "Replace scanner photo" : "Upload scanner photo"}
                <input hidden type="file" accept="image/png,image/jpeg" aria-label="Upload payment scanner photo" onChange={e => { uploadQr(e.target.files?.[0]); e.target.value = ""; }} />
              </Button>
              {settings.paymentQrImage && <Button onClick={removeQr} disabled={settingsBusy}>Remove scanner photo</Button>}
            </Stack>
            <Typography variant="caption">Scanner photos are saved immediately. Use Save payment & delivery for text instructions and delivery charges.</Typography>
          </Box>
          <FormControlLabel label="Delivery charges are confirmed" control={<Switch checked={settings.deliveryRulesConfirmed} onChange={e => setSettings({ ...settings, deliveryRulesConfirmed: e.target.checked })} />} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <EcommField label="Delivery charge (INR)" type="number" value={settings.deliveryCharge} inputProps={{ min: 0, step: "0.01" }} onChange={e => setSettings({ ...settings, deliveryCharge: e.target.value })} />
            <EcommField label="Free delivery above (INR)" type="number" value={settings.freeDeliveryThreshold} inputProps={{ min: 0, step: "0.01" }} onChange={e => setSettings({ ...settings, freeDeliveryThreshold: e.target.value })} helperText="0 gives free delivery to all orders." />
          </Stack>
          <Button onClick={saveSettings} disabled={settingsBusy} variant="contained" sx={{ alignSelf: "flex-start" }}>Save payment & delivery</Button>
        </Stack> : <Stack spacing={2}><Typography>{settingsError || "Loading payment settings…"}</Typography>
          {settingsError && <Button onClick={async () => {
            setSettingsError("");
            try { setSettings(await orderCrmApi.settings(businessId)); }
            catch (e) { setSettingsError(errorMessage(e)); }
          }}>Retry payment settings</Button>}
        </Stack>}</AccordionDetails>
      </Accordion>
  </Stack>;
}
